import "./Goal.scss";
import { useEffect, useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import NotAvailable from "./NotAvailable";
import Button from "app/storybookComponents/Button";
import GoalCard from "./GoalCard";
import DeleteGoalModal from "./Modals/DeleteGoalModal";
import ReflectionModal from "./Modals/ReflectionModal";
import { selectCurrentUserAccountId } from "../../containers/Global/slice";
import GoalsTable from "./table/GoalsTable";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDebounce } from "./hooks/useDebounce";
import { useGoalDeepLink } from "./hooks/useGoalDeepLink";
import GoalsSearchBar from "./GoalsSearchBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGetGoalsListQuery, useUpdateGoalMutation } from "./goalsApi";
import { GoalItem, GoalReflection } from "./types";
import OrganizationGoalsTable from "./table/OrganizationGoalsTable";
import Paginator from "../SortableTable/Paginator";
import RowsPerPageSelector from "../SortableTable/RowsPerPageSelector";
import GoalModal from "./Modals/GoalModal";
import { Separator } from "app/shadcnComponents/ui/separator";
import ArchivedTabSelector from "./ArchivedTabSelector";
import PillTabSelector from "./PillTabSelector";
import CustomChatbotButton from "app/containers/Chatbot/CustomChatbotButton";
import { selectCompanySettings } from "app/containers/AdminConsole/slice";
import {
  resetChatbotState,
  setCoachBotPrompt,
  setIsCoachbotOpen,
} from "app/containers/Chatbot/slice";
import { GOAL_COACH_BO_PROMPTS } from "./constants";
import { trackFeatureViewed } from "utils/trackingFunctions";
import { Skeleton } from "app/shadcnComponents/ui/skeleton";
import LinkedGoalsDrawer from "./LinkedGoalsDrawer";

interface GoalsProps {
  viewUserId: number | null;
  isAdmin?: boolean;
  showOrganizationGoals?: boolean;
  hideScopeTabs?: boolean;
}

export default function Goals({
  viewUserId,
  isAdmin,
  showOrganizationGoals,
  hideScopeTabs = false,
}: Readonly<GoalsProps>) {
  const dispatch = useAppDispatch();
  const [updateGoal] = useUpdateGoalMutation();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const isAdminConsolePath = useMemo(
    () => location.pathname.includes("AdminConsole"),
    [location]
  );

  //--------------------States--------------------
  const [goalScope, setGoalScope] = useState<"org" | "all">("org");
  const [filter, setFilter] = useState<string>("all");
  const [deleteGoalModalOpen, setDeleteGoalModalOpen] = useState(false);

  const [selectedGoal, setSelectedGoal] = useState<GoalItem | null>(null);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [reflectionModalOpen, setReflectionModalOpen] = useState(false);
  const [editReflectionModalOpen, setEditReflectionModalOpen] = useState(false);
  const [selectedReflection, setSelectedReflection] =
    useState<GoalReflection | null>(null);
  const [reflectionGoal, setReflectionGoal] = useState<GoalItem | null>(null);
  const [goalModalState, setGoalModalState] = useState<{
    isOpen: boolean;
    goal?: GoalItem;
  }>({ isOpen: false });
  const [expandedGoals, setExpandedGoals] = useState<Record<number, boolean>>(
    {}
  );
  const [linkedGoalModalSelectedGoal, setLinkedGoalModalSelectedGoal] =
    useState<GoalItem | null>(null);

  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("");
  const [status, setStatus] = useState("");
  const [visibility, setVisibility] = useState("");
  const [pageSelected, setPageSelected] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [isArchivedTabSelected, setIsArchivedTabSelected] = useState(false);

  const debouncedSearch = useDebounce(search, 400);

  //--------------------Selectors--------------------
  const loggedInUserId = useAppSelector(selectCurrentUserAccountId);
  const companySettings = useAppSelector(selectCompanySettings);

  // Decide which user to fetch
  const effectiveViewUserId: number | undefined =
    viewUserId ?? loggedInUserId ?? undefined;

  // Build filter params (without page) for deep linking
  const filterParams = useMemo(() => {
    return {
      filter,
      is_org_goal: showOrganizationGoals && goalScope === "org",
      isAllTab: showOrganizationGoals && goalScope === "all",
      viewUserId:
        !showOrganizationGoals && goalScope !== "all"
          ? effectiveViewUserId
          : undefined,
      departmentId: goalScope === "all" ? department || undefined : undefined,
      reflectionStatus: goalScope === "all" ? status || undefined : undefined,
      visibility: goalScope === "all" ? visibility || undefined : undefined,
      search: debouncedSearch || undefined,
      perPage: itemsPerPage,
      archived: isArchivedTabSelected,
    };
  }, [
    filter,
    goalScope,
    debouncedSearch,
    effectiveViewUserId,
    status,
    department,
    showOrganizationGoals,
    visibility,
    itemsPerPage,
    isArchivedTabSelected,
  ]);

  const highlightGoalId = searchParams.get("goalId")
    ? Number(searchParams.get("goalId"))
    : undefined;

  const { deepLinkReady, shouldSkipInitialQuery } = useGoalDeepLink(
    highlightGoalId,
    filterParams,
    setPageSelected
  );

  // Build query params (with page) for RTK Query
  const queryParams = useMemo(() => {
    return {
      page: pageSelected,
      ...filterParams,
    };
  }, [pageSelected, filterParams]);

  // RTK Query hook - automatically fetches and manages cache
  const useGetGoalsListQueryRes = useGetGoalsListQuery(queryParams, {
    skip: shouldSkipInitialQuery,
  });

  const useGetGoalsListQueryResForTabs = useGetGoalsListQuery(
    {
      ...queryParams,
      filter: "all",
    },
    {
      skip: shouldSkipInitialQuery,
    }
  );

  const {
    data: goalsData,
    isLoading: loading,
    isUninitialized,
    isError,
    error: queryError,
    isFetching,
  } = useGetGoalsListQueryRes;

  const isLoading = loading || isUninitialized;
  const goals = useMemo(() => goalsData?.data?.data ?? [], [goalsData]);
  const last_page = goalsData?.data?.last_page ?? 1;
  const error = isError ? (queryError as any)?.message : undefined;

  // -------------------- Effects --------------------
  useEffect(() => {
    if (hideScopeTabs) {
      setGoalScope("org");
    }
  }, [hideScopeTabs]);

  useEffect(() => {
    if (!highlightGoalId || !deepLinkReady) return;

    const el = document.getElementById(`goal-${highlightGoalId}`);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("goal-highlight");

    setTimeout(() => {
      el.classList.remove("goal-highlight");
    }, 2000);
  }, [highlightGoalId, deepLinkReady, goals]);

  // Reset to page 1 when filters change (RTK Query will auto-fetch)
  useEffect(() => {
    const isInvalidSearch =
      debouncedSearch &&
      debouncedSearch.length > 0 &&
      debouncedSearch.length < 3;

    if (isInvalidSearch) return;

    setPageSelected(1);
  }, [goalScope, filter, department, status, visibility, debouncedSearch]);

  useEffect(() => {
    setSearch("");
    setDepartment("");
    setStatus("");
    setVisibility("");
    setFilter("all");
  }, [goalScope]);

  useEffect(() => {
    trackFeatureViewed("Goal", {
      viewingOrgGoals: showOrganizationGoals || false,
      isAdmin: isAdmin || false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------- Derived States --------------------

  // Org goals → admin can edit    // Personal goals → only owner can edit
  const isReadonly =
    (showOrganizationGoals && !isAdmin) ||
    (!showOrganizationGoals &&
      loggedInUserId !== undefined &&
      viewUserId !== loggedInUserId);

  const isNoData = goals.length === 0 && !isLoading;

  const hasFilter =
    filter &&
    typeof filter === "string" &&
    filter.trim().toLowerCase() !== "all";

  const canShowSetGoalButton =
    (!showOrganizationGoals && !isReadonly) ||
    (showOrganizationGoals && isAdmin);

  const showSetGoalButton =
    canShowSetGoalButton &&
    // Always show in ALL tab
    ((showOrganizationGoals && goalScope === "all") ||
      // Show when there is data
      !isNoData);

  // -------------------- Handlers --------------------
  const getSelectedGoal = (goalId: number) => {
    const goal = goals.find((g) => g.goalId === goalId);
    return goal;
  };

  const openDeleteModal = (goalId: number) => {
    setSelectedGoalId(goalId);
    setDeleteGoalModalOpen(true);
  };

  const closeDeleteModal = () => setDeleteGoalModalOpen(false);

  const openReflectionModal = (goalId: number) => {
    const goal = getSelectedGoal(goalId);
    if (!goal) return;
    setReflectionGoal(goal);
    setReflectionModalOpen(true);
  };
  const closeReflectionModal = () => {
    setReflectionModalOpen(false);
  };

  const openSetGoalModal = () => {
    setGoalModalState({ isOpen: true });
  };

  const openEditReflectionModal = (reflection: GoalReflection) => {
    const goal = getSelectedGoal(reflection.goalId);
    if (!goal) return;
    setSelectedGoal(goal);
    setSelectedReflection(reflection);
    setEditReflectionModalOpen(true);
  };

  const closeEditReflectionModal = () => {
    setEditReflectionModalOpen(false);
    setSelectedReflection(null);
  };

  const onArchive = (goalId: number) => {
    const goal = getSelectedGoal(goalId);
    if (!goal) return;
    updateGoal({
      goalId,
      payload: {
        ...goal,
        archived: true,
      },
    });
  };

  const onUnarchive = (goalId: number) => {
    const goal = getSelectedGoal(goalId);
    if (!goal) return;
    updateGoal({
      goalId,
      payload: {
        ...goal,
        archived: false,
      },
    });
  };

  const getOrganizationGoalView = () => {
    const isTableLoading = isLoading || isFetching;
    if (
      showOrganizationGoals &&
      goalScope === "org" &&
      isNoData &&
      !hasFilter &&
      !search &&
      !highlightGoalId &&
      !error &&
      !isTableLoading
    ) {
      return (
        <>
          <div className="mt-[10px]" />
          <NotAvailable
            message="No organizational goals yet"
            subMessage="Create your first org-level goal to get started."
            onAction={
              canShowSetGoalButton
                ? () => setGoalModalState({ isOpen: true })
                : undefined
            }
            actionLabel={canShowSetGoalButton ? "Set a Goal" : undefined}
          />
        </>
      );
    }

    if (goalScope === "all") {
      return (
        <GoalsTable
          goals={goals}
          onEdit={(goal) => setGoalModalState({ goal, isOpen: true })}
          department={department}
          onDepartmentChange={setDepartment}
          status={status}
          onStatusChange={setStatus}
          visibility={visibility}
          onVisibilityChange={setVisibility}
          error={error}
          isLoading={isTableLoading}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onDelete={openDeleteModal}
        />
      );
    }

    if (isAdminConsolePath) {
      return (
        <OrganizationGoalsTable
          goals={goals}
          onEdit={(goal) => setGoalModalState({ goal, isOpen: true })}
          isLoading={isTableLoading}
          error={error}
          onReflect={openReflectionModal}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onDelete={openDeleteModal}
        />
      );
    }

    return getGoalCardsView();
  };

  const setIsCardExpanded = (goalId: number, expanded: boolean) => {
    setExpandedGoals((prev) => ({
      ...prev,
      [goalId]: expanded,
    }));
  };

  const getGoalCardsView = () => {
    return (
      <div className="d-flex flex-col border rounded">
        {goals.map((goal, index) => (
          <div id={`goal-${goal.goalId}`} key={goal.goalId}>
            <GoalCard
              goal={goal}
              readonly={isReadonly}
              onEdit={(goal) => setGoalModalState({ goal, isOpen: true })}
              onDelete={openDeleteModal}
              onReflect={openReflectionModal}
              onEditReflection={openEditReflectionModal}
              canSetGoal={canShowSetGoalButton}
              isCardExpanded={expandedGoals[goal.goalId] || false}
              setIsCardExpanded={(expanded) =>
                setIsCardExpanded(goal.goalId, expanded)
              }
              setLinkedGoalModalSelectedGoal={setLinkedGoalModalSelectedGoal}
            />
            {index < goals.length - 1 && <Separator />}
          </div>
        ))}
      </div>
    );
  };

  const getGoalsList = () => {
    if (showOrganizationGoals) {
      return getOrganizationGoalView();
    }

    if (isLoading || isFetching) {
      return (
        <div className="d-flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={`skeleton-${index}`} className="h-20 w-full" />
          ))}
        </div>
      );
    }

    if (isNoData) {
      return (
        <NotAvailable
          message="No goals yet"
          subMessage="Set your personal or team-aligned goals to get started."
          onAction={canShowSetGoalButton ? openSetGoalModal : undefined}
          actionLabel={canShowSetGoalButton ? "Set a Goal" : undefined}
        />
      );
    }

    return getGoalCardsView();
  };

  const getPagination = () => {
    return (
      <div className="mt-3 d-flex justify-content-between align-items-center">
        <Paginator
          pageRangeDisplayed={5}
          pageCount={last_page}
          onPageChange={(selectedItem) => {
            setPageSelected(selectedItem.selected + 1);
          }}
          forcePage={pageSelected - 1}
        />
        <RowsPerPageSelector
          value={itemsPerPage}
          onChange={(value) => {
            if (value !== itemsPerPage) {
              setPageSelected(1);
            }
            setItemsPerPage(value);
          }}
          availableRowsPerPage={[5, 10, 25, 50, 100]}
        />
      </div>
    );
  };

  const getFilterTabs = () => {
    const stats = useGetGoalsListQueryResForTabs?.data?.stats;
    return (
      <PillTabSelector
        availableTabs={[
          {
            label: "All",
            count: stats?.all ?? 0,
            value: "all",
          },
          {
            label: "Active",
            count: stats?.active ?? 0,
            value: "active",
          },
          {
            label: "Past",
            count: stats?.past ?? 0,
            value: "past",
          },
          {
            label: "Work",
            count: stats?.byType?.work ?? 0,
            value: "work",
          },
          {
            label: "Team",
            count: stats?.byType?.team ?? 0,
            value: "team",
          },
          {
            label: "Personal",
            count: stats?.byType?.personal ?? 0,
            value: "personal",
          },
          {
            label: "Needs Attention",
            count: stats?.byReflectionStatus?.unlikely_to_reach ?? 0,
            value: "unlikely_to_reach",
          },
        ]}
        selectedTab={filter}
        onTabChange={(tab) => setFilter(tab)}
      />
    );
  };

  const onCoachbotClick = () => {
    dispatch(resetChatbotState());
    const { userPrompts, coachBotIntro } =
      GOAL_COACH_BO_PROMPTS[showOrganizationGoals ? "admin" : "user"];

    dispatch(
      setCoachBotPrompt({
        userPrompts,
        coachBotIntro,
        type: "goals",
      })
    );
    dispatch(setIsCoachbotOpen(true));
  };

  const getHeader = () => {
    if (!showOrganizationGoals || hideScopeTabs) {
      return (
        <div>
          <div className="d-flex justify-content-between flex-wrap gap-2">
            <div className="d-flex gap-2 align-items-center">
              <div>
                <h3 className="text-black">Goals</h3>
              </div>
              <ArchivedTabSelector
                availableTabs={[{ label: "Active" }, { label: "Archived" }]}
                selectedTab={isArchivedTabSelected ? "Archived" : "Active"}
                onTabChange={(tab) => {
                  setIsArchivedTabSelected(tab === "Archived");
                }}
              />
            </div>
            {showSetGoalButton && (
              <div className="d-flex gap-2">
                <GoalsSearchBar search={search} onSearchChange={setSearch} />
                <Button onClick={openSetGoalModal} className="!rounded-md ">
                  <FontAwesomeIcon icon={"plus"} className="me-2" />
                  {showOrganizationGoals
                    ? "Set an Organizational Goal"
                    : "Set a Personal Goal"}
                </Button>
              </div>
            )}
          </div>
          <Separator className="my-[16px] mt-[20px]" />
          {getFilterTabs()}
          <div className="mb-[20px]" />
        </div>
      );
    }

    return (
      <>
        <div className="goals-header pb-2">
          <div className="goals-title">Goals</div>
          {showSetGoalButton && (
            <Button
              onClick={openSetGoalModal}
              variant="secondary-blue"
              className="set-goal-btn"
              type="button"
            >
              <span>+</span>{" "}
              {showOrganizationGoals
                ? "Set an Organizational Goal"
                : "Set a Personal Goal"}
            </Button>
          )}
        </div>

        {showOrganizationGoals && !hideScopeTabs && (
          <div className="goals-scope-tabs">
            <button
              className="no-style-button"
              onClick={() => setGoalScope("org")}
            >
              <div
                className={`scope-tab ${goalScope === "org" ? "active" : ""}`}
              >
                Org Goals
              </div>
            </button>

            <button
              className="no-style-button"
              onClick={() => setGoalScope("all")}
            >
              <div
                className={`scope-tab ${goalScope === "all" ? "active" : ""}`}
              >
                All Goals
              </div>
            </button>
          </div>
        )}
        <div className="d-flex justify-content-between flex-wrap gap-2">
          <div className="d-flex gap-2 align-items-center">
            <ArchivedTabSelector
              availableTabs={[{ label: "Active" }, { label: "Archived" }]}
              selectedTab={isArchivedTabSelected ? "Archived" : "Active"}
              onTabChange={(tab) => {
                setIsArchivedTabSelected(tab === "Archived");
              }}
            />
          </div>
          <div className="d-flex gap-2">
            <GoalsSearchBar search={search} onSearchChange={setSearch} />
          </div>
        </div>

        {(!showOrganizationGoals || goalScope !== "all") && (
          <>
            <Separator className="my-[10px]" />
            {getFilterTabs()}
          </>
        )}
      </>
    );
  };

  return (
    <div className="goals-container">
      <div className="goals-wrapper">
        {/* HEADER */}
        {getHeader()}
        {getGoalsList()}
        {getPagination()}
      </div>
      {/* ----------- Modals ----------- */}
      <DeleteGoalModal
        isOpen={deleteGoalModalOpen}
        hideModal={closeDeleteModal}
        goalId={selectedGoalId}
      />
      <ReflectionModal
        isOpen={reflectionModalOpen}
        hideModal={closeReflectionModal}
        goal={reflectionGoal}
        key={`reflectionModal-${reflectionGoal?.goalId}`}
      />
      <ReflectionModal
        isOpen={editReflectionModalOpen}
        hideModal={closeEditReflectionModal}
        goal={selectedGoal}
        reflection={selectedReflection}
        key={`editReflectionModal-${selectedReflection?.goalsReflectionsId}`}
      />
      <GoalModal
        isOpen={goalModalState.isOpen}
        goal={goalModalState.goal}
        hideModal={() => setGoalModalState({ isOpen: false })}
        isOrgGoal={showOrganizationGoals ?? false}
        goalScope={goalScope}
        key={`goalModal-${goalModalState.goal?.goalId}`}
      />
      {companySettings?.aiFeaturesEnabled &&
      (viewUserId === loggedInUserId || (showOrganizationGoals && isAdmin)) ? (
        <CustomChatbotButton
          onCoachbotClick={onCoachbotClick}
          text={`Need help with goals?`}
        />
      ) : null}
      <LinkedGoalsDrawer
        isOpen={!!linkedGoalModalSelectedGoal}
        onClose={() => setLinkedGoalModalSelectedGoal(null)}
        parentGoal={linkedGoalModalSelectedGoal}
        closeDrawerOnChildClick
      />
    </div>
  );
}
